import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, KeyOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersService } from '../../api/users';
import { CreateUserRequest, UpdateUserRequest, UserRole } from '../../types';

const { Title } = Typography;

interface UserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  password?: string;
  confirmPassword?: string;
}

const UserEdit: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation();
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);
  const queryClient = useQueryClient();
  const isEditMode = Boolean(id);

  // Fetch user data for editing
  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersService.getUserById(id!),
    enabled: isEditMode,
  });

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: usersService.createUser,
    onSuccess: () => {
      message.success('Пользователь успешно создан');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Ошибка при создании пользователя');
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: usersService.updateUser,
    onSuccess: () => {
      message.success('Пользователь успешно обновлен');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      navigate('/users');
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Ошибка при обновлении пользователя');
    },
  });

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (password: string) => usersService.resetPassword(id!, password),
    onSuccess: () => {
      message.success('Пароль успешно обновлен');
      passwordForm.resetFields();
      setIsPasswordFormVisible(false);
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Ошибка при обновлении пароля');
    },
  });

  // Fill form with user data when editing
  useEffect(() => {
    if (user && isEditMode) {
      form.setFieldsValue({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
      });
    }
  }, [user, form, isEditMode]);

  const handleSubmit = async (values: UserFormValues) => {
    try {
      if (isEditMode) {
        // Для обновления убираем поля пароля
        const updateData: UpdateUserRequest = {
          id: id!,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          isActive: values.isActive,
        };
        console.log('Update data being sent:', updateData);
        await updateMutation.mutateAsync(updateData);
      } else {
        // Для создания пароль обязателен
        if (!values.password) {
          message.error('Пароль обязателен для создания пользователя');
          return;
        }
        const createData: CreateUserRequest = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          isActive: values.isActive,
          password: values.password,
        };
        await createMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by mutation
    }
  };

  const handlePasswordReset = async (values: { password: string; confirmPassword: string }) => {
    try {
      await resetPasswordMutation.mutateAsync(values.password);
    } catch (error) {
      // Error is handled by mutation
    }
  };

  const validatePassword = (_: unknown, value: string) => {
    if (!isEditMode && !value) {
      return Promise.reject(t('validation.enterPassword'));
    }
    if (value && value.length < 8) {
      return Promise.reject(t('validation.minLength', { count: 8 }));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = (_: unknown, value: string) => {
    const password = form.getFieldValue('password');
    if (value && value !== password) {
      return Promise.reject(t('validation.passwordsNotMatch'));
    }
    return Promise.resolve();
  };

  // Валидация для формы смены пароля
  const validateNewPassword = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(t('validation.enterNewPassword'));
    }
    if (value.length < 8) {
      return Promise.reject(t('validation.minLength', { count: 8 }));
    }
    return Promise.resolve();
  };

  const validateNewConfirmPassword = (_: unknown, value: string) => {
    const password = passwordForm.getFieldValue('password');
    if (!value) {
      return Promise.reject(t('validation.confirmNewPassword'));
    }
    if (value !== password) {
      return Promise.reject(t('validation.passwordsNotMatch'));
    }
    return Promise.resolve();
  };

  if (isEditMode && isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/users')}
          >
            {t('common.backToList')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Title level={3}>
          {isEditMode ? t('users.editUser') : t('users.createUser')}
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 600 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label={t('users.firstName')}
                rules={[
                  { required: true, message: t('validation.enterName') },
                  { min: 2, message: t('validation.minLength', { count: 2 }) },
                ]}
              >
                <Input placeholder={t('placeholders.enterName')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label={t('users.lastName')}
                rules={[
                  { required: true, message: t('validation.enterLastName') },
                  { min: 2, message: t('validation.minLength', { count: 2 }) },
                ]}
              >
                <Input placeholder={t('placeholders.enterLastName')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label={t('users.email')}
            rules={[
              { required: true, message: t('validation.enterEmail') },
              { type: 'email', message: t('validation.invalidEmailFormat') },
            ]}
          >
            <Input placeholder={t('placeholders.enterEmail')} />
          </Form.Item>

          <Form.Item
            name="role"
            label={t('users.role')}
            rules={[{ required: true, message: t('validation.selectRole') }]}
          >
            <Select placeholder={t('placeholders.selectRole')}>
              <Select.Option value="SUPERADMIN">{t('userRoles.SUPERADMIN')}</Select.Option>
              <Select.Option value="ADMIN">{t('userRoles.ADMIN')}</Select.Option>
              <Select.Option value="OPERATOR">{t('userRoles.OPERATOR')}</Select.Option>
              <Select.Option value="TRANSLATOR">{t('userRoles.TRANSLATOR')}</Select.Option>
              <Select.Option value="SUPPORT">{t('userRoles.SUPPORT')}</Select.Option>
              <Select.Option value="READONLY">{t('userRoles.READONLY')}</Select.Option>
            </Select>
          </Form.Item>

          {!isEditMode && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label={t('users.password')}
                  rules={[{ validator: validatePassword }]}
                >
                  <Input.Password placeholder={t('placeholders.enterPassword')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirmPassword"
                  label={t('users.confirmPassword')}
                  rules={[
                    {
                      validator: validateConfirmPassword,
                    },
                  ]}
                >
                  <Input.Password placeholder={t('placeholders.repeatPassword')} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item style={{ marginTop: 32 }}>
            <Row gutter={16}>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={createMutation.isPending || updateMutation.isPending}
                  size="large"
                >
                  {isEditMode ? t('common.update') : t('common.create')}
                </Button>
              </Col>
              <Col>
                <Button
                  size="large"
                  onClick={() => navigate('/users')}
                >
                  {t('common.cancel')}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>

      {/* Отдельный блок смены пароля для редактирования */}
      {isEditMode && (
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <KeyOutlined />
              {t('users.resetPassword')}
            </div>
          }
          style={{ marginTop: 16 }}
          extra={
            !isPasswordFormVisible && (
              <Button 
                type="primary" 
                ghost
                icon={<KeyOutlined />}
                onClick={() => setIsPasswordFormVisible(true)}
              >
                {t('users.changePassword')}
              </Button>
            )
          }
        >
          {!isPasswordFormVisible ? (
            <p style={{ margin: 0, color: '#666' }}>
              {t('users.changePasswordDesc')}
            </p>
          ) : (
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordReset}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="password"
                    label={t('users.newPassword')}
                    rules={[{ validator: validateNewPassword }]}
                  >
                    <Input.Password placeholder={t('placeholders.enterNewPassword')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="confirmPassword"
                    label={t('users.confirmPassword')}
                    rules={[{ validator: validateNewConfirmPassword }]}
                  >
                    <Input.Password placeholder={t('placeholders.repeatNewPassword')} />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item>
                <Row gutter={16}>
                  <Col>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<KeyOutlined />}
                      loading={resetPasswordMutation.isPending}
                    >
                      {t('users.resetPassword')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => {
                        setIsPasswordFormVisible(false);
                        passwordForm.resetFields();
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </Form>
          )}
        </Card>
      )}
    </div>
  );
};

export default UserEdit;
